import { useContext, useEffect, useState } from 'react';
import { BREAKPOINTS_WIDTHS, useWindowSize } from '@belong/common';
import { Button, ButtonBase, Text } from '@belong/ui';
import { IconCaretDown, IconCheck2 } from '@belong/icons';
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  MenuPopover,
} from '@reach/menu-button';
import { ApiClientContext } from 'common/contexts/api-client';
import ModalV2, { MODAL_TYPES } from 'components/Modal/ModalV2/ModalV2';
import { CopyToClipboardButton } from 'pages/Accounts/Components/copy-to-clipboard-button';
import AccountPageSection from 'pages/Accounts/Pages/AccountPage/AccountPageSection/AccountPageSection';
import { areExternalPaymentInstructionsValid } from '../FailedPayments/failed-payments-utils';
import type { ConsumerWireInstructionsModel } from '../FailedPayments/failed-payments.types';
import styles from './bank-information.module.css';

type HomeBankInformation = {
  homeUniqueId: string;
  homeAddress: string;
  status: 'Available' | 'Unavailable';
  wire?: ConsumerWireInstructionsModel;
  zelle?: ConsumerWireInstructionsModel;
};
type BankInformation = {
  status: 'Available' | 'NoHome';
  homes: HomeBankInformation[];
};
type HomeImages = Record<string, string | undefined>;
type Method = 'wire' | 'zelle';
const methods: {
  method: Method;
  label: string;
  icon: string;
  iconClassName: string;
}[] = [
  {
    method: 'wire',
    label: 'Bank Wire Information',
    icon: 'bank-wire',
    iconClassName: 'items-start pt-[2px]',
  },
  {
    method: 'zelle',
    label: 'Zelle Account',
    icon: 'zelle',
    iconClassName: 'items-center',
  },
];
const titles = {
  wire: 'Belong Bank Wire Information',
  zelle: 'Belong Zelle Account',
};

function CopyField({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.field}>
      <p className={styles.fieldText}>
        <span className="font-semibold">{label}: </span>
        {value}
      </p>
      <CopyToClipboardButton value={value} ariaLabel={`Copy ${label}`} />
    </div>
  );
}

function BankInformationDetails({
  method,
  onClose,
  homeImages,
}: {
  method: Method;
  onClose: () => void;
  homeImages?: HomeImages;
}) {
  const { width } = useWindowSize();
  const isMobile = width < BREAKPOINTS_WIDTHS.MD;
  const apiClient = useContext(ApiClientContext) as {
    get: <T>(url: string) => Promise<T>;
  };
  const [information, setInformation] = useState<BankInformation | null>(null);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [selectedHomeId, setSelectedHomeId] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    setInformation(null);
    setError(false);
    apiClient.get<BankInformation>('/v2/users/bank-information').then(
      (result: BankInformation) => {
        if (!cancelled) setInformation(result);
      },
      () => {
        if (!cancelled) setError(true);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [apiClient, attempt]);
  const selectedHome =
    information?.homes.find((home) => home.homeUniqueId === selectedHomeId) ??
    information?.homes.find((home) => home.status === 'Available') ??
    information?.homes[0];
  const details = selectedHome?.[method];
  const instructionsValid = areExternalPaymentInstructionsValid(
    details,
    method === 'wire' ? 'WireTransfer' : 'ZelleTransfer',
  );
  const rows: [string, string | null | undefined][] = details
    ? method === 'wire'
      ? [
          ['Account Holder', details.accountHolder],
          ['Bank', details.bankName],
          ['Bank Address', details.bankAddress],
          ['Routing Number', details.routingNumber],
          ['Account Number', details.accountNumber],
          ['Reference Code', details.referenceCode],
        ]
      : [
          ['Recipient', details.recipient],
          ['Reason', details.note],
        ]
    : [];
  return (
    <ModalV2
      show
      onHide={onClose}
      noPadding
      type={isMobile ? MODAL_TYPES.FULL_PAGE : MODAL_TYPES.STANDARD}
      noFullPageHeader
      title={<h2>{titles[method]}</h2>}
      titleClassName={styles.title}
      closeButtonClassName={styles.closeButton}
      className={styles.dialog}
      contentClassName={styles.surface}
    >
      <div className={styles.body}>
        {method === 'wire' && information?.homes.length ? (
          <BankHomePicker
            homes={information.homes}
            selectedHome={selectedHome!}
            homeImages={homeImages}
            onSelect={setSelectedHomeId}
          />
        ) : null}
        <div className={styles.fields}>
          {error ? (
            <div role="alert">
              <Text>We couldn’t load the bank information.</Text>
              <ButtonBase
                className="mt-md font-semibold text-navy underline"
                onClick={() => setAttempt((value) => value + 1)}
              >
                Try again
              </ButtonBase>
            </div>
          ) : !information ? (
            <Text role="status">Loading bank information…</Text>
          ) : selectedHome?.status !== 'Available' || !instructionsValid ? (
            <Text role="status">
              Bank information is not available for this home. Please contact
              your Belong team for help.
            </Text>
          ) : (
            rows.map(([label, value]) => (
              <CopyField
                key={`${selectedHome?.homeUniqueId}-${label}`}
                label={label}
                value={value!}
              />
            ))
          )}
        </div>
      </div>
      <div className={styles.footer}>
        <p className={styles.support}>
          Questions or concerns?{' '}
          <a
            href="/support"
            className="font-semibold transition-colors duration-hover hover:text-dark-navy focus-visible:text-dark-navy"
          >
            LET’S TALK
          </a>
        </p>
        <Button
          size={{ _: 'fluid', md: 'default' }}
          className="shrink-0"
          onClick={onClose}
        >
          Done
        </Button>
      </div>
    </ModalV2>
  );
}

function BankHomePicker({
  homes,
  selectedHome,
  homeImages,
  onSelect,
}: {
  homes: HomeBankInformation[];
  selectedHome: HomeBankInformation;
  homeImages?: HomeImages;
  onSelect: (id: string) => void;
}) {
  const homeLabel = (
    <span className={styles.homeButtonContent}>
      <span className={styles.homeLabel}>
        {homeImages?.[selectedHome.homeUniqueId] && (
          <img
            alt=""
            className={styles.homeImage}
            src={homeImages[selectedHome.homeUniqueId]}
          />
        )}
        <span className="min-w-0 break-words text-left font-semibold">
          {selectedHome.homeAddress}
        </span>
      </span>
      {homes.length > 1 && (
        <IconCaretDown width={15} height={8} className="shrink-0" />
      )}
    </span>
  );
  if (homes.length < 2) {
    return <div className={styles.picker}>{homeLabel}</div>;
  }
  return (
    <div className={styles.picker}>
      <Menu>
        <MenuButton
          as={ButtonBase}
          aria-label={`Select home: ${selectedHome.homeAddress}`}
          className={styles.homeButton}
        >
          {homeLabel}
        </MenuButton>
        <MenuPopover portal={false} className={styles.homeMenu}>
          <MenuItems className="mt-xs flex max-h-[300px] max-w-[90vw] flex-col overflow-y-auto rounded border border-solid border-gray bg-white p-sm shadow-lg">
            {homes.map((home) => (
              <MenuItem
                key={home.homeUniqueId}
                className="cursor-pointer"
                onSelect={() => onSelect(home.homeUniqueId)}
              >
                <span className="flex items-center justify-between gap-xl py-sm text-navy">
                  {home.homeAddress}
                  {home.homeUniqueId === selectedHome.homeUniqueId && (
                    <IconCheck2
                      width={16}
                      height={16}
                      className="shrink-0 text-green"
                    />
                  )}
                </span>
              </MenuItem>
            ))}
          </MenuItems>
        </MenuPopover>
      </Menu>
    </div>
  );
}

export function BankInformationSection({
  homeImages,
}: {
  homeImages?: HomeImages;
}) {
  const [method, setMethod] = useState<Method | null>(null);
  return (
    <>
      <AccountPageSection
        title="Belong Bank Information"
        content={
          <div className="flex flex-col gap-lg">
            {methods.map((item) => (
              <ButtonBase
                key={item.method}
                className="flex w-full items-center justify-between gap-sm text-left text-navy transition-colors duration-hover hover:text-dark-navy active:text-dark-navy focus-visible:text-dark-navy"
                onClick={() => setMethod(item.method)}
              >
                <span className="flex items-center gap-sm">
                  <span
                    className={`flex h-[24px] w-[24px] shrink-0 justify-center ${item.iconClassName}`}
                  >
                    <img
                      alt=""
                      src={`/icons/bank-information/${item.icon}.svg`}
                    />
                  </span>
                  {item.label}
                </span>
                <span className="hidden font-semibold sm:inline">VIEW</span>
                <span
                  aria-hidden="true"
                  className="flex h-[14px] w-[8px] shrink-0 items-center justify-center sm:hidden"
                >
                  <IconCaretDown
                    width={14}
                    height={8}
                    className="shrink-0 -rotate-90"
                  />
                </span>
              </ButtonBase>
            ))}
          </div>
        }
      />
      {method && (
        <BankInformationDetails
          key={method}
          method={method}
          homeImages={homeImages}
          onClose={() => setMethod(null)}
        />
      )}
    </>
  );
}
